import type { ComponentType } from 'react'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import {
  SibsEntryDataWidget,
  SibsEntityReferenceEmailsWidget,
  SibsCaseReportWidget,
  SibsPipelineWidget,
  type SibsEntry,
} from './SibsWidgets'
import { PayloadWidget } from '../Entries/PayloadWidget'

type SibsWidgetProps = { entry: SibsEntry; theme: ProcessedTheme }

export type SibsWidgetDefinition = {
  id: string
  label: string
  description: string
  accent: 'sky' | 'red' | 'amber' | 'emerald' | 'none'
  Component: ComponentType<SibsWidgetProps>
}

// Adapter: PayloadWidget takes originalPayload/parsedPayload directly rather
// than {entry,theme} — both fields live on entry.raw_entry_original_payload
// / entry.raw_entry_parsed_payload, so this is a pure prop-shape shim.
function PayloadWidgetAdapter({ entry, theme }: SibsWidgetProps) {
  return (
    <PayloadWidget
      originalPayload={entry.raw_entry_original_payload}
      parsedPayload={entry.raw_entry_parsed_payload}
      theme={theme}
    />
  )
}

// SibsEmailWidget and OriginalSubmissionWidget are intentionally NOT in this
// registry: both need parent-owned state/callbacks (sentIds/onSent, and a
// Turnstile-gated getScreenshotToken) that don't fit the registry's plain
// {entry,theme} contract. They remain permanent hardcoded panels in
// SibsEntryDetailPage (SibsMailView.tsx).
export const SIBS_WIDGET_LIBRARY: SibsWidgetDefinition[] = [
  { id: 'entry-data', label: 'Entry Data', description: 'Key/value table of the raw entry fields', accent: 'none', Component: SibsEntryDataWidget },
  { id: 'entity-reference-emails', label: 'Entity Reference · Previous Emails', description: 'Other emails previously sent for the same entity reference', accent: 'sky', Component: SibsEntityReferenceEmailsWidget },
  { id: 'case-report', label: 'New Fraud Type · SIBS Validation', description: 'Report a new fraud typology to the SIBS helpdesk for validation', accent: 'amber', Component: SibsCaseReportWidget },
  { id: 'pipeline', label: 'Pipeline Steps', description: "Collapsible list of this entry's processing pipeline steps", accent: 'emerald', Component: SibsPipelineWidget },
  { id: 'payload', label: 'Payload', description: 'Original vs agentic-parsed payload diff table', accent: 'none', Component: PayloadWidgetAdapter },
]

// Workbench (AdminSubmitEntryView) submits synthetic/test entries, not real
// fraud cases — SibsCaseReportWidget dispatches a real email to SIBS's
// actual fraud helpdesk, which is the wrong action to expose there (an
// operator could accidentally send a real external report built from test
// data). Same library minus that one widget; everything else is read-only.
export const WORKBENCH_WIDGET_LIBRARY: SibsWidgetDefinition[] = SIBS_WIDGET_LIBRARY.filter((w) => w.id !== 'case-report')
